import { isDefined, isNonEmpty } from '@sgme/fp';
import type { SgwtHelpCenterHTMLElement } from '@sgwt/sgwt-widgets-react';
import { SgwtHelpCenter } from '@sgwt/sgwt-widgets-react';
import { lifeRingModalMapStateToProps } from 'components/ActionBar/LifeRing/LifeRingModal/LifeRingModal.connect';
import { useFetchHelpCenterAvailableTags } from 'hooks/use-fetch-help-center-tags';
import { useToggleFeature } from 'hooks/useToggleFeature';
import { useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { helpCenterActions } from 'state/helpRequest/actions';
import { getCurrentContext, getTopicToOpen, isOpen } from 'state/helpRequest/selectors';
import type { HelpCenterContext } from 'state/helpRequest/state';
import { getUserPreferencesLanguage } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { getUserLogin } from 'state/user';
import { nowISODateString } from 'utils/date';

interface StateProps extends HelpCenterContext {
  additionalInformation?: Record<string, unknown>;

  isOpen: boolean;

  topicToOpen: string | undefined;

  login: string;

  lang: string;
}
interface DispatchProps {
  onOpen: () => void;
  onClose: () => void;
}
interface Props extends DispatchProps, StateProps {}
const mapStateToProps = (state: AppState): StateProps => {
  const {
    isinCode,
    productKey,
    externalQuoteId,
    orderId,
    pricerId,
    productId,
    quoteId,
    tradeId,
    fileCode,
    workflowErrors,
    batchColumns,
    negotiationId,
  } = lifeRingModalMapStateToProps(state);
  const additionalInformation: Record<string, unknown> = {
    'ISIN Code': isinCode,
    'File Code': fileCode,
    'Product key': productKey,
    'External quote id': externalQuoteId,
    'Order id': orderId,
    'Pricer id': pricerId,
    'Product id': productId,
    'Quote id': quoteId,
    'Trade id': tradeId,
    'Negotiation id': negotiationId,

    Errors: workflowErrors
      ?.map((error, i) => {
        const filteredEntries = Object.entries(error).filter(([, v]) => isNonEmpty(v));
        if (filteredEntries.length === 0) {
          return undefined;
        }
        const details = filteredEntries.map(([k, v]) => `${k} : ${v}`).join('\n');
        return `\nERROR #${i + 1}: \n${details}`;
      })
      .filter(isDefined)
      .join('\n'),
  };

  if (Array.isArray(batchColumns)) {
    batchColumns.forEach(({ negotiationId, quoteId }, index) => {
      additionalInformation[`Negotiation id form${index + 1}`] = negotiationId;
      additionalInformation[`Quote id form${index + 1}`] = quoteId;
    });
  }
  return {
    ...getCurrentContext(state),
    additionalInformation,
    isOpen: isOpen(state),
    topicToOpen: getTopicToOpen(state),
    login: getUserLogin(state),
    lang: getUserPreferencesLanguage(state),
  };
};
const mapDispatchToProps = (dispatch: ThunkDispatch): DispatchProps => {
  return {
    onClose() {
      dispatch(helpCenterActions.close());
    },
    onOpen() {
      dispatch(helpCenterActions.open('unmanaged'));
    },
  };
};
export const ConnectedHelpCenter = connect(mapStateToProps, mapDispatchToProps)(HelpCenter);

// Source = https://developer.sgmarkets.com/docs/web/widgets/structural-widgets/sgwt-help-center.html
type HelpCenterNextEvent = CustomEvent<{ action: string; categoryId: string; topicId: string; title: string }>;
type HelpCenterTopicEvent = CustomEvent<{ topicId: string }>;
type HelpCenterCategoryEvent = CustomEvent<{ categoryId: string }>;

function loadWebComponent() {
  import('webcomponents/tutorial-video');
  import('webcomponents/tutorial-pdf-download');
}

function onHelpCenterNext(event: Event) {
  const detail = (event as HelpCenterNextEvent).detail;

  logAnalytics.trackEvent('Help Center', `Click on "${detail.categoryId ?? detail.topicId}"`, detail.title);
}

function onHelpCenterTopic(event: Event) {
  const detail = (event as HelpCenterTopicEvent).detail;

  logAnalytics.trackEvent('Help Center', `Click on "${detail.topicId}"`);
}

function onHelpCenterCategory(event: Event) {
  const detail = (event as HelpCenterCategoryEvent).detail;

  logAnalytics.trackEvent('Help Center', `Click on "${detail.categoryId}"`);
}
function HelpCenter({
  messageTemplate,
  isOpen,
  topicToOpen,
  onOpen,
  onClose,
  lang,
  login,
  additionalInformation,
}: Props): React.JSX.Element {
  const ref = useRef<SgwtHelpCenterHTMLElement>(null);
  const { data } = useFetchHelpCenterAvailableTags();
  const enhancementEnabled = useToggleFeature('helpCenterEnhancement');
  useEffect(() => {
    if (enhancementEnabled && additionalInformation) {
      ref.current?.setAdditionalMessageInformation?.(
        Object.fromEntries(Object.entries(additionalInformation).filter(([, v]) => isDefined(v))),
      );
    }
  }, [enhancementEnabled, additionalInformation]);
  useEffect(() => {
    const messageTopics = data?.map(t => ({ topic: t.label }));
    if (enhancementEnabled && messageTopics) {
      ref.current?.setMessageTopics?.(messageTopics);
    }
  }, [enhancementEnabled, data]);

  const onHelpCenterClosed = useCallback(() => {
    logAnalytics.trackEvent('Help Center', 'Close Help Center');
    onClose();
  }, [onClose]);

  const onHelpCenterOpened = useCallback(() => {
    logAnalytics.trackEvent('Help Center', 'Open Help Center');
    onOpen();
  }, [onOpen]);

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const currentRef = ref.current;

    currentRef.addEventListener?.('sgwt-help-center--open', loadWebComponent);
    currentRef.addEventListener?.('sgwt-help-center--close', onHelpCenterClosed);
    currentRef.addEventListener?.('sgwt-help-center--next', onHelpCenterNext);
    currentRef.addEventListener?.('sgwt-help-center--topic', onHelpCenterTopic);
    currentRef.addEventListener?.('sgwt-help-center--category', onHelpCenterCategory);
    currentRef.addEventListener?.('sgwt-help-center--form', onHelpCenterOpened);
    return () => {
      if (currentRef) {
        currentRef.removeEventListener?.('sgwt-help-center--open', loadWebComponent);
        currentRef.removeEventListener?.('sgwt-help-center--close', onHelpCenterClosed);
        currentRef.removeEventListener?.('sgwt-help-center--next', onHelpCenterNext);
        currentRef.removeEventListener?.('sgwt-help-center--topic', onHelpCenterTopic);
        currentRef.removeEventListener?.('sgwt-help-center--category', onHelpCenterCategory);
        currentRef.removeEventListener?.('sgwt-help-center--form', onHelpCenterOpened);
      }
    };
  }, [onHelpCenterOpened, onHelpCenterClosed]);

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    if (isOpen) {
      ref.current.form?.();
    }
    if (isDefined(topicToOpen)) {
      ref.current.topic?.(topicToOpen);
    }
  }, [isOpen, topicToOpen]);

  const env = window.sgmeConfiguration.envLabel?.toLocaleUpperCase() ?? 'PROD';
  const onReady = useCallback((element: SgwtHelpCenterHTMLElement) => {
    ref.current = element;
  }, []);

  return (
    <SgwtHelpCenter
      applicationId="sgme-sp"
      id="sp-help-center"
      introductionTour={false}
      mailSubject={`[SGME-SP:${env}] Help center message by ${login} at ${nowISODateString()}`}
      sg-connect-support="sg-connect-v2"
      messageTemplate={messageTemplate}
      language={lang}
      onWidgetReady={onReady}
    />
  );
}
